import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  CircleAlert,
  CreditCard,
  Database,
  Loader2,
  RefreshCw,
  Search,
  SearchX,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { hasGroupAdmin } from "@/lib/pagePermissions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const PAGE_SIZE = 50;

export type Recommendation = "increase" | "decrease" | "maintain" | "review" | "pending" | "failed";

export interface CreditCheckCustomer {
  account: string;
  customerName: string;
  terms: string | null;
  currentCreditLimit: number | null;
  currency: string | null;
  creditsafeConnectId: string | null;
  status: string | null;
  lastCheckedAt: string | null;
  recommendedCreditLimit: number | null;
  recommendation: string | null;
  reasons: string[];
  riskScore: number | null;
  riskBand: string | null;
  reviewDecision: string | null;
  lastError: string | null;
  updatedAt: string | null;
}

interface CreditChecksResponse {
  provider: { configured: boolean; mode: "sandbox" | "production"; message: string };
  phase: number;
  merlinUpdatesEnabled: boolean;
  summary: Record<Recommendation | "total", number>;
  customers: CreditCheckCustomer[];
}

export interface Portfolio {
  id: number;
  name: string;
  isDefault: boolean;
  isShared: boolean;
}

export interface CompanyCandidate {
  id: string;
  name: string;
  registrationNumber: string | null;
  status: string | null;
  address: string | null;
  postcode: string | null;
}

const recommendationStyles: Record<Recommendation, string> = {
  increase: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  decrease: "bg-red-50 text-red-700 ring-red-600/10",
  maintain: "bg-blue-50 text-blue-700 ring-blue-600/20",
  review: "bg-amber-50 text-amber-800 ring-amber-600/20",
  pending: "bg-slate-100 text-slate-700 ring-slate-500/10",
  failed: "bg-red-50 text-red-700 ring-red-600/10",
};

export function customerRecommendation(customer: Pick<CreditCheckCustomer, "recommendation" | "lastError">): Recommendation {
  const value = customer.recommendation?.toLowerCase();
  if (value === "increase" || value === "decrease" || value === "maintain" || value === "review" || value === "pending" || value === "failed") {
    return value as Recommendation;
  }
  return customer.lastError ? "failed" : "pending";
}

export function formatCreditMoney(amount: number | null, currency: string | null): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency || ""}`.trim();
  }
}

export function formatCheckDate(value: string | null): string {
  if (!value) return "Not checked";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export function matchesCreditCheckFilter(customer: CreditCheckCustomer, search: string, filter: "all" | Recommendation): boolean {
  const query = search.trim().toLocaleLowerCase();
  const matchesSearch = !query || [customer.account, customer.customerName, customer.creditsafeConnectId]
    .filter(Boolean)
    .some((value) => value!.toLocaleLowerCase().includes(query));
  return matchesSearch && (filter === "all" || customerRecommendation(customer) === filter);
}

function RecommendationBadge({ customer }: { customer: CreditCheckCustomer }) {
  const recommendation = customerRecommendation(customer);
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${recommendationStyles[recommendation]}`}>
      {recommendation}
    </span>
  );
}

function SummaryCard({ label, value, recommendation }: { label: string; value: number; recommendation?: Recommendation }) {
  const textColor = recommendation ? recommendationStyles[recommendation].split(" ")[1] : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${textColor}`}>{value}</p>
    </div>
  );
}

function LinkCreditsafeDialog({
  customer,
  onClose,
  portfolios,
}: {
  customer: CreditCheckCustomer | null;
  onClose: () => void;
  portfolios: Portfolio[];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const defaultPortfolioId = portfolios.find(p => p.isDefault)?.id;
  const initialPortfolioId = defaultPortfolioId ? String(defaultPortfolioId) : (portfolios[0] ? String(portfolios[0].id) : "");

  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>(initialPortfolioId);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>("");

  useEffect(() => {
    if (customer) {
      setSelectedCandidateId("");
      if (!selectedPortfolioId && portfolios.length > 0) {
        setSelectedPortfolioId(initialPortfolioId);
      }
    }
  }, [customer, portfolios, initialPortfolioId, selectedPortfolioId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["creditsafe-search", customer?.account],
    queryFn: async () => {
      if (!customer) return null;
      const response = await fetch(`${BASE}/api/credit-checks/${encodeURIComponent(customer.account)}/search-creditsafe`, {
        method: "POST",
        credentials: "include"
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Search failed");
      }
      return response.json() as Promise<{ account: string, customerName: string, candidates: CompanyCandidate[] }>;
    },
    enabled: !!customer,
    refetchOnWindowFocus: false,
  });

  const addMutation = useMutation({
    mutationFn: async ({ portfolioId, connectId }: { portfolioId: number, connectId: string }) => {
      if (!customer) throw new Error("No customer selected");
      const response = await fetch(`${BASE}/api/credit-checks/${encodeURIComponent(customer.account)}/add-to-portfolio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ portfolioId, connectId })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to add to portfolio");
      }
      return response.json() as Promise<{ success: boolean, alreadyPresent: boolean, connectId: string, portfolioId: number }>;
    },
    onSuccess: (data) => {
      if (data.alreadyPresent) {
        toast({ title: "Already linked", description: "This customer was already in the portfolio." });
      } else {
        toast({ title: "Customer linked successfully" });
      }
      queryClient.invalidateQueries({ queryKey: ["credit-checks"] });
      onClose();
    },
    onError: (mutationError: Error) => {
      toast({ title: "Failed to link customer", description: mutationError.message, variant: "destructive" });
    }
  });

  return (
    <Dialog open={!!customer} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 sm:rounded-2xl">
        <div className="p-6 pb-5 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-900">Match CreditSafe Entity</DialogTitle>
            <DialogDescription className="text-slate-500 mt-1.5 leading-relaxed">
              {customer ? (
                <>
                  Select the correct legal entity for <span className="font-medium text-slate-900">{customer.customerName}</span> <span className="font-mono text-xs">({customer.account})</span>.
                </>
              ) : "Select a customer to link."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-6 custom-scrollbar">
          <div className="space-y-2.5">
            <label className="text-sm font-semibold text-slate-900">Target Portfolio</label>
            {portfolios.length > 0 ? (
              <Select value={selectedPortfolioId} onValueChange={setSelectedPortfolioId}>
                <SelectTrigger className="w-full sm:w-[320px] bg-white border-slate-200 shadow-sm">
                  <SelectValue placeholder="Select a portfolio" />
                </SelectTrigger>
                <SelectContent>
                  {portfolios.map(p => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name} {p.isDefault ? "(Default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                No CreditSafe portfolios are available. Close this window and retry after portfolio access is configured.
              </p>
            )}
          </div>

          <div className="space-y-2.5">
            <label className="text-sm font-semibold text-slate-900 flex justify-between">
              <span>Candidate Matches</span>
              {data?.candidates && <span className="text-slate-500 font-medium">{data.candidates.length} found</span>}
            </label>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mb-4 text-primary" />
                  <p className="text-sm font-medium">Querying CreditSafe database...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 text-red-600 px-4 text-center bg-red-50/50">
                  <AlertCircle className="h-8 w-8 mb-3" />
                  <p className="text-sm font-semibold">Search failed</p>
                  <p className="text-xs mt-1 text-red-600/80">{(error as Error).message}</p>
                </div>
              ) : !data?.candidates || data.candidates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <SearchX className="h-10 w-10 mb-3 text-slate-300" />
                  <p className="text-sm font-medium text-slate-700">No candidates found</p>
                  <p className="text-xs mt-1 text-slate-500">We couldn't find any companies matching this customer.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto custom-scrollbar">
                  {data.candidates.map(candidate => (
                    <label
                      key={candidate.id}
                      className={`flex items-start gap-4 p-4 cursor-pointer transition-colors ${
                        selectedCandidateId === candidate.id
                          ? "bg-primary/5 relative after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-primary"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="pt-0.5 pl-1">
                        <input
                          type="radio"
                          name="candidate"
                          className="h-4 w-4 border-slate-300 text-primary focus:ring-primary"
                          value={candidate.id}
                          checked={selectedCandidateId === candidate.id}
                          onChange={() => setSelectedCandidateId(candidate.id)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-semibold truncate ${selectedCandidateId === candidate.id ? "text-primary-900" : "text-slate-900"}`}>
                            {candidate.name}
                          </p>
                          <span className="shrink-0 rounded-md bg-white px-2 py-0.5 text-[11px] font-mono font-medium text-slate-600 border border-slate-200">
                            {candidate.id}
                          </span>
                        </div>
                        <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Registration</p>
                            <p className="font-medium text-slate-700">{candidate.registrationNumber || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status</p>
                            <p className="font-medium text-slate-700">{candidate.status || "—"}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Address</p>
                            <p className="text-slate-600 text-xs leading-relaxed line-clamp-2">
                              {[candidate.address, candidate.postcode].filter(Boolean).join(", ") || "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 bg-white">
          <p className="mb-3 text-xs text-slate-500">
            Confirming adds the selected legal entity to the sandbox portfolio. It does not update Merlin.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={onClose} disabled={addMutation.isPending}>Cancel</Button>
            <Button
              disabled={!selectedCandidateId || !selectedPortfolioId || addMutation.isPending}
              onClick={() => {
                addMutation.mutate({
                  portfolioId: Number(selectedPortfolioId),
                  connectId: selectedCandidateId
                });
              }}
            >
              {addMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add to sandbox portfolio
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CreditChecksPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | Recommendation>("all");
  const [page, setPage] = useState(1);
  const [matchingCustomer, setMatchingCustomer] = useState<CreditCheckCustomer | null>(null);

  const isAdmin = hasGroupAdmin(user, "cash_receipts");

  const { data, isLoading, error } = useQuery<CreditChecksResponse>({
    queryKey: ["credit-checks"],
    queryFn: async () => {
      const response = await fetch(`${BASE}/api/credit-checks`, { credentials: "include" });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Unable to load credit checks");
      }
      return response.json();
    },
  });

  const { data: portfoliosData, error: portfoliosError } = useQuery({
    queryKey: ["creditsafe-portfolios"],
    queryFn: async () => {
      const response = await fetch(`${BASE}/api/credit-checks/creditsafe/portfolios`, { credentials: "include" });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load portfolios");
      }
      return response.json() as Promise<{ portfolios: Portfolio[], mode: string }>;
    },
    enabled: isAdmin,
  });

  const portfolios = portfoliosData?.portfolios ?? [];

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${BASE}/api/credit-checks/refresh-customers`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Unable to refresh customers");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["credit-checks"] });
      toast({ title: "Customer credit-check data refreshed" });
    },
    onError: (mutationError: Error) => toast({ title: mutationError.message, variant: "destructive" }),
  });

  const customers = data?.customers ?? [];
  const filteredCustomers = useMemo(
    () => customers.filter((customer) => matchesCreditCheckFilter(customer, search, filter)),
    [customers, search, filter],
  );
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const visibleCustomers = filteredCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [search, filter]);
  useEffect(() => setPage((current) => Math.min(current, totalPages)), [totalPages]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <ShieldCheck className="h-6 w-6 text-primary" />
              Credit Checks
            </h1>
            <p className="mt-1 text-sm text-slate-500">Monitor customer credit readiness and recommended limits.</p>
          </div>
          {isAdmin && (
            <Button onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}>
              {refreshMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh customers
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex min-h-72 items-center justify-center rounded-2xl border border-slate-200 bg-white">
            <Loader2 className="h-7 w-7 animate-spin text-primary" aria-label="Loading credit checks" />
          </div>
        ) : error ? (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
            <div className="flex items-center gap-2 font-semibold"><AlertCircle className="h-5 w-5" /> Credit checks could not be loaded</div>
            <p className="mt-1 text-sm">{(error as Error).message}</p>
          </div>
        ) : data && (
          <>
            <div className={`rounded-2xl border p-5 ${data.provider.configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <div className="flex flex-wrap items-start gap-3">
                {data.provider.configured ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-700" /> : <CircleAlert className="mt-0.5 h-5 w-5 text-amber-700" />}
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">CreditSafe {data.provider.configured ? "connected" : "not connected"}</p>
                  <p className="mt-1 text-sm text-slate-700">{data.provider.message}</p>
                  {!data.provider.configured && <p className="mt-2 text-sm font-medium text-amber-900">Recommendations will begin when CreditSafe is connected.</p>}
                </div>
                {data.provider.configured && <span className="rounded-full bg-white/80 px-2.5 py-1 text-xs font-semibold capitalize text-slate-900 ring-1 ring-inset ring-slate-200 shadow-sm">{data.provider.mode}</span>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className={`rounded-xl border p-5 ${data.phase >= 1 ? "border-blue-200 bg-blue-50/50" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex gap-3">
                  <Database className={`h-5 w-5 ${data.phase >= 1 ? "text-blue-700" : "text-slate-500"}`} />
                  <div>
                    <p className={`font-semibold text-sm ${data.phase >= 1 ? "text-blue-900" : "text-slate-700"}`}>Phase 1: Eligible customer register</p>
                    <p className={`mt-1 text-xs leading-relaxed ${data.phase >= 1 ? "text-blue-800/80" : "text-slate-600"}`}>
                      Merlin customers are listed here without changing their accounts or credit limits.
                    </p>
                  </div>
                </div>
              </div>
              <div className={`rounded-xl border p-5 ${data.phase >= 2 ? "border-indigo-200 bg-indigo-50/50" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex gap-3">
                  <Sparkles className={`h-5 w-5 ${data.phase >= 2 ? "text-indigo-700" : "text-slate-500"}`} />
                  <div>
                    <p className={`font-semibold text-sm ${data.phase >= 2 ? "text-indigo-900" : "text-slate-700"}`}>Phase 2: CreditSafe portfolio pilot</p>
                    <p className={`mt-1 text-xs leading-relaxed ${data.phase >= 2 ? "text-indigo-800/80" : "text-slate-600"}`}>
                      {data.phase >= 2
                        ? "Admins can verify a legal entity and add it to the CreditSafe sandbox. Merlin updates remain disabled."
                        : "Sandbox portfolio matching is not enabled yet."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {isAdmin && portfoliosError && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <p className="font-semibold">CreditSafe portfolios could not be loaded</p>
                <p className="mt-1">{(portfoliosError as Error).message}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              <SummaryCard label="Total" value={data.summary.total} />
              <SummaryCard label="Pending" value={data.summary.pending} recommendation="pending" />
              <SummaryCard label="Increase" value={data.summary.increase} recommendation="increase" />
              <SummaryCard label="Decrease" value={data.summary.decrease} recommendation="decrease" />
              <SummaryCard label="Maintain" value={data.summary.maintain} recommendation="maintain" />
              <SummaryCard label="Review" value={data.summary.review} recommendation="review" />
              <SummaryCard label="Failed" value={data.summary.failed} recommendation="failed" />
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-200 p-4 sm:flex-row sm:items-center">
                <label className="relative flex-1 max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <span className="sr-only">Search customers</span>
                  <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customer, account or CreditSafe ID" className="h-9 w-full rounded-md border border-slate-200 pl-9 pr-3 text-sm shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-slate-400" />
                </label>
                <label className="text-sm text-slate-700 flex-shrink-0">
                  <span className="sr-only">Filter recommendation</span>
                  <select value={filter} onChange={(event) => setFilter(event.target.value as "all" | Recommendation)} className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary sm:w-44 text-sm font-medium text-slate-700">
                    <option value="all">All recommendations</option>
                    {(["increase", "decrease", "maintain", "review", "pending", "failed"] as Recommendation[]).map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}
                  </select>
                </label>
              </div>

              {customers.length === 0 ? (
                <div className="p-16 text-center text-slate-500">
                  <CreditCard className="mx-auto mb-4 h-10 w-10 text-slate-300" />
                  <p className="font-medium text-slate-900">No customers available</p>
                  <p className="mt-1 text-sm text-slate-500">Customer records will appear here once available to the credit-check service.</p>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="p-16 text-center text-sm text-slate-500 font-medium">No customers match the current search and filter.</div>
              ) : (
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full min-w-[1250px]">
                    <thead className="bg-slate-50/80 border-b border-slate-200 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3 whitespace-nowrap">Customer</th>
                        <th className="px-4 py-3 whitespace-nowrap">Terms</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Current limit</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Recommended</th>
                        <th className="px-4 py-3 whitespace-nowrap">Recommendation</th>
                        <th className="px-4 py-3 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 whitespace-nowrap">Risk</th>
                        <th className="px-4 py-3 whitespace-nowrap">Last checked</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">CreditSafe</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleCustomers.map((customer) => (
                        <tr key={customer.account} className="align-top hover:bg-slate-50/80 transition-colors">
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{customer.customerName}</p>
                            <p className="mt-0.5 text-xs text-slate-500 font-mono">{customer.account}</p>
                            {customer.lastError && (
                              <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-red-600">
                                <XCircle className="h-3.5 w-3.5" />
                                {customer.lastError}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{customer.terms || "—"}</td>
                          <td className="px-4 py-3 text-right text-sm text-slate-700 tabular-nums">
                            {formatCreditMoney(customer.currentCreditLimit, customer.currency)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-slate-900 tabular-nums">
                            {formatCreditMoney(customer.recommendedCreditLimit, customer.currency)}
                          </td>
                          <td className="px-4 py-3">
                            <RecommendationBadge customer={customer} />
                            {customer.reasons.length > 0 && (
                              <p className="mt-1.5 max-w-48 text-[11px] leading-snug text-slate-500">
                                {customer.reasons.join(" · ")}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">
                            {customer.status || "—"}
                            {customer.reviewDecision ? (
                              <p className="mt-1 text-[11px] text-slate-500 font-medium">
                                {customer.reviewDecision}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">
                            {customer.riskScore != null ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="font-semibold text-slate-900 tabular-nums">{customer.riskScore}</span>
                                {customer.riskBand && <span className="text-xs text-slate-500">({customer.riskBand})</span>}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap tabular-nums">
                            <time dateTime={customer.lastCheckedAt || undefined}>
                              {formatCheckDate(customer.lastCheckedAt)}
                            </time>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {customer.creditsafeConnectId ? (
                              <div className="flex flex-col items-end gap-1.5">
                                <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Linked
                                </span>
                                <span className="text-[10px] font-mono text-slate-400 font-medium">
                                  {customer.creditsafeConnectId}
                                </span>
                              </div>
                            ) : isAdmin ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                className="h-7 text-xs bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900 text-slate-600 shadow-sm"
                                onClick={() => setMatchingCustomer(customer)}
                              >
                                <Search className="mr-1.5 h-3 w-3" />
                                Find match
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400 font-medium">Unlinked</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm text-slate-600 bg-slate-50/50 border-t border-slate-200">
                    <span className="font-medium text-slate-700">
                      Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredCustomers.length)} of {filteredCustomers.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1}>
                        Previous
                      </Button>
                      <span className="min-w-[5rem] text-center font-medium text-slate-700">Page {page} of {totalPages}</span>
                      <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={page === totalPages}>
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <LinkCreditsafeDialog
        customer={matchingCustomer}
        onClose={() => setMatchingCustomer(null)}
        portfolios={portfolios}
      />
    </Layout>
  );
}
